
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollArea } from "@/components/ui/scroll-area";
import { PatientFileRow } from "./PatientFileRow";
import { Input } from "@/components/ui/input";
import { Search, Filter, Users, SlidersHorizontal } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export const PatientFileList = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [sortField, setSortField] = useState<string>("created_at");
  
  // Chargement des dossiers patients
  const { data: files, isLoading } = useQuery({
    queryKey: ["patient-files", sortOrder, sortField],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("patient_files")
        .select("*")
        .order(sortField, { ascending: sortOrder === "asc" });

      if (error) throw error;
      return data;
    },
  });

  // Filtrer les dossiers en fonction du terme de recherche
  const filteredFiles = files?.filter((file) => {
    const searchValue = searchTerm.toLowerCase();
    return (
      file.file_number?.toLowerCase().includes(searchValue) ||
      file.last_name?.toLowerCase().includes(searchValue) ||
      file.first_name?.toLowerCase().includes(searchValue) ||
      file.email?.toLowerCase().includes(searchValue) ||
      file.phone_number?.toLowerCase().includes(searchValue)
    );
  });

  if (isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-16 w-full" />
        ))}
      </div>
    );
  }

  if (!files?.length) {
    return (
      <Card className="flex items-center justify-center p-8 border-dashed border-2">
        <div className="text-center">
          <Users className="h-12 w-12 text-muted-foreground mb-3 mx-auto" />
          <h3 className="font-semibold text-lg mb-1">Aucun dossier patient</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Créez votre premier dossier patient en cliquant sur "Nouveau dossier".
          </p>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Rechercher un dossier..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-8"
          />
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="icon" title="Filtrer et trier">
              <SlidersHorizontal className="h-4 w-4" />
              <span className="sr-only">Filtres</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>Trier par</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuCheckboxItem
              checked={sortField === "created_at"}
              onCheckedChange={() => setSortField("created_at")}
            >
              Date de création
            </DropdownMenuCheckboxItem>
            <DropdownMenuCheckboxItem
              checked={sortField === "last_name"}
              onCheckedChange={() => setSortField("last_name")}
            >
              Nom
            </DropdownMenuCheckboxItem>
            <DropdownMenuCheckboxItem
              checked={sortField === "file_number"}
              onCheckedChange={() => setSortField("file_number")}
            >
              Numéro de dossier
            </DropdownMenuCheckboxItem>
            <DropdownMenuSeparator />
            <DropdownMenuLabel>Ordre</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuCheckboxItem
              checked={sortOrder === "asc"}
              onCheckedChange={() => setSortOrder(sortOrder === "asc" ? "desc" : "asc")}
            >
              Croissant
            </DropdownMenuCheckboxItem>
            <DropdownMenuCheckboxItem
              checked={sortOrder === "desc"}
              onCheckedChange={() => setSortOrder(sortOrder === "desc" ? "asc" : "desc")}
            >
              Décroissant
            </DropdownMenuCheckboxItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      
      <ScrollArea className="h-[600px] pr-4">
        <div className="space-y-3">
          {filteredFiles && filteredFiles.length > 0 ? (
            filteredFiles.map((file) => (
              <PatientFileRow key={file.id} file={file} />
            ))
          ) : (
            <Card className="flex items-center justify-center p-6">
              <p className="text-sm text-muted-foreground">
                Aucun dossier patient ne correspond à votre recherche.
              </p>
            </Card>
          )}
        </div>
      </ScrollArea>
      <div className="text-sm text-muted-foreground flex items-center justify-between px-2">
        <span>
          {filteredFiles?.length} dossier{filteredFiles?.length !== 1 ? 's' : ''} trouvé{filteredFiles?.length !== 1 ? 's' : ''}
        </span>
        <span className="text-xs text-muted-foreground/60">
          Dernière synchronisation: {new Date().toLocaleTimeString()}
        </span>
      </div>
    </div>
  );
};
